import {
  type ComponentProps,
  type FormEvent,
  type FormEventHandler,
  type HTMLAttributes,
  type KeyboardEventHandler,
  useRef,
  useState,
} from 'react';
import {
  faArrowTurnDownLeft,
  faCircleNotch,
  faSquare,
  faXmark,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { faStop } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';

export type ChatStatus = 'submitted' | 'streaming' | 'error';

import { Icon } from '@/components/ui/icon';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea,
} from '@/components/ui/input-group';
import { cn } from '@/lib/utils';

export type PromptInputMessage = {
  text: string;
};

export type PromptInputProps = Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit'> & {
  onSubmit: (
    message: PromptInputMessage,
    event: FormEvent<HTMLFormElement>,
  ) => void | Promise<void>;
  isLoading?: boolean;
};

export const PromptInput = ({
  className,
  onSubmit,
  isLoading = false,
  children,
  ...props
}: PromptInputProps) => {
  const formRef = useRef<HTMLFormElement | null>(null);

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    if (isLoading) {
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    const text = (formData.get('message') as string) || '';

    if (!text.trim()) {
      return;
    }

    form.reset();

    try {
      const result = onSubmit({ text }, event);

      if (result instanceof Promise) {
        result.catch(() => {});
      }
    } catch {}
  };

  return (
    <form className={cn('w-full', className)} onSubmit={handleSubmit} ref={formRef} {...props}>
      <InputGroup className="overflow-hidden">{children}</InputGroup>
    </form>
  );
};

export type PromptInputTextareaProps = ComponentProps<typeof InputGroupTextarea> & {
  isLoading?: boolean;
};

export const PromptInputTextarea = ({
  onChange,
  className,
  placeholder = 'What would you like to know?',
  isLoading = false,
  ...props
}: PromptInputTextareaProps) => {
  const [isComposing, setIsComposing] = useState(false);

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === 'Enter') {
      if (isComposing || e.nativeEvent.isComposing) {
        return;
      }
      if (e.shiftKey) {
        return;
      }
      e.preventDefault();

      const form = e.currentTarget.form;
      const submitButton = form?.querySelector('button[type="submit"]') as HTMLButtonElement | null;
      if (submitButton?.disabled) {
        return;
      }

      form?.requestSubmit();
    }
  };

  return (
    <InputGroupTextarea
      className={cn(
        'field-sizing-content max-h-48 min-h-16 px-0 py-2 text-foreground font-normal',
        className,
      )}
      name="message"
      onCompositionEnd={() => setIsComposing(false)}
      onCompositionStart={() => setIsComposing(true)}
      onKeyDown={handleKeyDown}
      onChange={onChange}
      placeholder={placeholder}
      {...props}
    />
  );
};

export type PromptInputFooterProps = Omit<ComponentProps<typeof InputGroupAddon>, 'align'>;

export const PromptInputFooter = ({ className, ...props }: PromptInputFooterProps) => (
  <InputGroupAddon
    align="block-end"
    className={cn('justify-between gap-1', className)}
    {...props}
  />
);

export type PromptInputSubmitProps = ComponentProps<typeof InputGroupButton> & {
  status?: ChatStatus;
  isLoading?: boolean;
  onStop?: () => void;
};

export const PromptInputSubmit = ({
  className,
  variant = 'solid',
  size = 'icon-sm',
  status,
  isLoading = false,
  onStop,
  children,
  ...props
}: PromptInputSubmitProps) => {
  if (isLoading && onStop) {
    return (
      <InputGroupButton
        aria-label="Stop"
        className={cn(className)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onStop();
        }}
        size={size}
        type="button"
        variant="outline"
        {...props}
      >
        {children ?? <Icon icon={faStop} />}
      </InputGroupButton>
    );
  }

  let SubmitIcon = <Icon icon={faArrowTurnDownLeft} />;

  if (status === 'submitted') {
    SubmitIcon = <Icon icon={faCircleNotch} className="size-4 animate-spin" />;
  } else if (status === 'streaming') {
    SubmitIcon = <Icon icon={faSquare} />;
  } else if (status === 'error') {
    SubmitIcon = <Icon icon={faXmark} />;
  }

  return (
    <InputGroupButton
      aria-label="Submit"
      className={cn(className)}
      size={size}
      type="submit"
      variant={variant}
      {...props}
    >
      {children ?? SubmitIcon}
    </InputGroupButton>
  );
};
